import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Key,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  Trash2,
  Plus,
  Webhook,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  Clock,
  Globe,
  History,
  XCircle
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import api from '../../services/api';

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  last_used_at: string | null;
  created_at: string;
  expires_at: string | null;
  is_active: boolean;
  scopes: string[];
}

interface WebhookEndpoint {
  id: string;
  url: string;
  events: string[];
  is_active: boolean;
  secret: string;
  last_triggered_at: string | null;
  failure_count: number;
  created_at: string;
}

interface WebhookEvent {
  id: string;
  event_type: string;
  payload: any;
  status: 'success' | 'failed' | 'pending';
  response_code: number | null;
  error_message: string | null;
  duration_ms: number | null;
  created_at: string;
}

const AVAILABLE_EVENTS = [
  'lead.created',
  'lead.updated',
  'campaign.created',
  'campaign.updated',
  'campaign.deleted',
  'campaign.status_changed',
  'content.generated',
  'content.published',
  'keyword.added',
  'keyword.position_changed',
  'competitor.added',
  'report.generated',
  'subscription.created',
  'subscription.cancelled',
  'landing_page.created',
  'landing_page.published'
];

const MCP_SERVER_URL = 'https://marketing-api.devskin.com/api/mcp/rpc';

const mcpClaudeCmd = (key: string) =>
  `claude mcp add --transport http devskin-marketing ${MCP_SERVER_URL} --header "Authorization: Bearer ${key}"`;

const mcpJsonConfig = (key: string) => `{
  "mcpServers": {
    "devskin-marketing": {
      "type": "http",
      "url": "${MCP_SERVER_URL}",
      "headers": { "Authorization": "Bearer ${key}" }
    }
  }
}`;

const APISettings: React.FC = () => {
  const { t } = useTranslation();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookEndpoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showKeyValue, setShowKeyValue] = useState<string | null>(null);
  const [newKeyModalOpen, setNewKeyModalOpen] = useState(false);
  const [newWebhookModalOpen, setNewWebhookModalOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  // MCP section: which API key to embed in the config snippets. The full key value
  // only exists right after create/regenerate (it's hashed server-side), so we cache
  // those full values by id and let the user pick which key to paste into the snippets.
  const [knownKeyValues, setKnownKeyValues] = useState<Record<string, string>>({});
  const [selectedMcpKeyId, setSelectedMcpKeyId] = useState<string>('');

  // New API Key form
  const [newKeyData, setNewKeyData] = useState({
    name: '',
    expires_in_days: 0, // 0 = never
    scopes: ['read', 'write']
  });

  // New Webhook form
  const [newWebhookData, setNewWebhookData] = useState({
    url: '',
    events: [] as string[]
  });

  // Webhook events
  const [eventsModalOpen, setEventsModalOpen] = useState(false);
  const [selectedWebhookForEvents, setSelectedWebhookForEvents] = useState<WebhookEndpoint | null>(null);
  const [webhookEvents, setWebhookEvents] = useState<WebhookEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [keysRes, webhooksRes] = await Promise.all([
        api.get('/api-keys'),
        api.get('/outgoing-webhooks')
      ]);

      if (keysRes.data.success) {
        setApiKeys(keysRes.data.data || []);
      }
      if (webhooksRes.data.success) {
        setWebhooks(webhooksRes.data.data || []);
      }
    } catch (error) {
      console.error('Error loading API settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!newKeyData.name.trim()) {
      toast.error(t('settings.api.nameRequired', 'Name is required'));
      return;
    }

    setIsCreating(true);
    try {
      const response = await api.post('/api-keys', newKeyData);
      if (response.data.success) {
        const fullKey = response.data.data.key;
        const created = response.data.data.apiKey;
        setCreatedKey(fullKey);
        setApiKeys([...apiKeys, created]);
        // Cache the full value and auto-select it in the MCP snippets.
        if (created?.id) { setKnownKeyValues((m) => ({ ...m, [created.id]: fullKey })); setSelectedMcpKeyId(created.id); }
        setNewKeyData({ name: '', expires_in_days: 0, scopes: ['read', 'write'] });
        toast.success(t('settings.api.keyCreated', 'API key created successfully'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.generic'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteApiKey = async (id: string) => {
    if (!confirm(t('settings.api.confirmDelete', 'Are you sure you want to delete this API key?'))) return;

    try {
      await api.delete(`/api-keys/${id}`);
      setApiKeys(apiKeys.filter(k => k.id !== id));
      toast.success(t('settings.api.keyDeleted', 'API key deleted'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleRegenerateKey = async (id: string) => {
    if (!confirm(t('settings.api.confirmRegenerate', 'Are you sure? The old key will stop working immediately.'))) return;

    try {
      const response = await api.post(`/api-keys/${id}/regenerate`);
      if (response.data.success) {
        setCreatedKey(response.data.data.key);
        setKnownKeyValues((m) => ({ ...m, [id]: response.data.data.key }));
        setSelectedMcpKeyId(id);
        loadData();
        toast.success(t('settings.api.keyRegenerated', 'API key regenerated'));
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleCreateWebhook = async () => {
    if (!newWebhookData.url.trim()) {
      toast.error(t('settings.api.urlRequired', 'URL is required'));
      return;
    }
    if (newWebhookData.events.length === 0) {
      toast.error(t('settings.api.eventsRequired', 'Select at least one event'));
      return;
    }

    setIsCreating(true);
    try {
      const response = await api.post('/outgoing-webhooks', newWebhookData);
      if (response.data.success) {
        setWebhooks([...webhooks, response.data.data]);
        setNewWebhookModalOpen(false);
        setNewWebhookData({ url: '', events: [] });
        toast.success(t('settings.api.webhookCreated', 'Webhook created successfully'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.generic'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteWebhook = async (id: string) => {
    if (!confirm(t('settings.api.confirmDeleteWebhook', 'Are you sure you want to delete this webhook?'))) return;

    try {
      await api.delete(`/outgoing-webhooks/${id}`);
      setWebhooks(webhooks.filter(w => w.id !== id));
      toast.success(t('settings.api.webhookDeleted', 'Webhook deleted'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleToggleWebhook = async (id: string, isActive: boolean) => {
    try {
      await api.patch(`/outgoing-webhooks/${id}`, { is_active: !isActive });
      setWebhooks(webhooks.map(w => w.id === id ? { ...w, is_active: !isActive } : w));
      toast.success(isActive ? t('settings.api.webhookDisabled', 'Webhook disabled') : t('settings.api.webhookEnabled', 'Webhook enabled'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleTestWebhook = async (id: string) => {
    try {
      const response = await api.post(`/outgoing-webhooks/${id}/test`);
      if (response.data.success) {
        toast.success(t('settings.api.testSent', 'Test webhook sent!'));
        // Reload events if modal is open
        if (eventsModalOpen && selectedWebhookForEvents?.id === id) {
          handleViewEvents(selectedWebhookForEvents);
        }
      }
    } catch (error) {
      toast.error(t('settings.api.testFailed', 'Test webhook failed'));
    }
  };

  const handleViewEvents = async (webhook: WebhookEndpoint) => {
    setSelectedWebhookForEvents(webhook);
    setEventsModalOpen(true);
    setLoadingEvents(true);
    try {
      const response = await api.get(`/outgoing-webhooks/${webhook.id}/events`);
      if (response.data.success) {
        setWebhookEvents(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading webhook events:', error);
      setWebhookEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('common.copied', 'Copied to clipboard'));
  };

  const copyMcp = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('settings.mcp.copied', 'Copiado!'));
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-48 skeleton rounded-xl" />
        <div className="h-48 skeleton rounded-xl" />
      </div>
    );
  }

  // API key to embed in the MCP snippets: the full value if known (just created/regenerated),
  // otherwise the placeholder.
  const mcpKey = (selectedMcpKeyId && knownKeyValues[selectedMcpKeyId]) || 'SUA_API_KEY';

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* API Documentation Link */}
      <div className="p-4 bg-gradient-to-r from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20 rounded-xl border border-primary-200 dark:border-primary-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/50 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">
                {t('settings.api.documentation', 'API Documentation')}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('settings.api.documentationDesc', 'Learn how to integrate with our API')}
              </p>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => window.open('https://marketing.devskin.com/api-docs.html', '_blank')}
            rightIcon={<ExternalLink className="w-4 h-4" />}
          >
            {t('settings.api.viewDocs', 'View Docs')}
          </Button>
        </div>
      </div>

      {/* MCP — Claude Code / Codex / Cursor */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            {t('settings.mcp.title', 'MCP — Claude Code / Codex / Cursor')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('settings.mcp.intro', 'Conecte o servidor MCP da plataforma ao Claude Code, Codex ou Cursor para ter controle total da plataforma diretamente pela sua ferramenta de IA. Você precisa de uma API key criada na seção "API Keys" acima nesta página.')}
            </p>

            {/* API key selector — fills the snippets below with the chosen key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.mcp.selectKey', 'API key para usar no código')}
              </label>
              {apiKeys.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('settings.mcp.noKeys', 'Crie uma API key na seção "API Keys" acima — ela já será preenchida aqui automaticamente.')}
                </p>
              ) : (
                <>
                  <select
                    value={selectedMcpKeyId}
                    onChange={(e) => setSelectedMcpKeyId(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                  >
                    <option value="">{t('settings.mcp.selectKeyPlaceholder', 'Selecione uma API key…')}</option>
                    {apiKeys.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.name} ({k.key_prefix}…){knownKeyValues[k.id] ? ' ✓' : ''}
                      </option>
                    ))}
                  </select>
                  {selectedMcpKeyId && !knownKeyValues[selectedMcpKeyId] && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                      {t('settings.mcp.keyHidden', 'Por segurança, a chave completa só aparece ao criar/regenerar. Crie uma nova chave (ou regenere esta) para preencher o código automaticamente.')}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Server URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.mcp.serverUrl', 'URL do servidor MCP')}
              </label>
              <div className="flex items-start gap-2">
                <pre className="flex-1 p-3 bg-gray-900 text-gray-100 rounded-lg text-xs font-mono overflow-auto whitespace-pre-wrap">{MCP_SERVER_URL}</pre>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyMcp(MCP_SERVER_URL)}
                  title={t('settings.mcp.copy', 'Copiar')}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Claude Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.mcp.claudeCode', 'Claude Code (CLI)')}
              </label>
              <div className="flex items-start gap-2">
                <pre className="flex-1 p-3 bg-gray-900 text-gray-100 rounded-lg text-xs font-mono overflow-auto whitespace-pre-wrap">{mcpClaudeCmd(mcpKey)}</pre>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyMcp(mcpClaudeCmd(mcpKey))}
                  title={t('settings.mcp.copy', 'Copiar')}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Codex / .mcp.json */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.mcp.codex', 'Codex / .codex/config e .mcp.json genérico')}
              </label>
              <div className="flex items-start gap-2">
                <pre className="flex-1 p-3 bg-gray-900 text-gray-100 rounded-lg text-xs font-mono overflow-auto whitespace-pre-wrap">{mcpJsonConfig(mcpKey)}</pre>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyMcp(mcpJsonConfig(mcpKey))}
                  title={t('settings.mcp.copy', 'Copiar')}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Cursor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.mcp.cursor', 'Cursor (~/.cursor/mcp.json)')}
              </label>
              <div className="flex items-start gap-2">
                <pre className="flex-1 p-3 bg-gray-900 text-gray-100 rounded-lg text-xs font-mono overflow-auto whitespace-pre-wrap">{mcpJsonConfig(mcpKey)}</pre>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyMcp(mcpJsonConfig(mcpKey))}
                  title={t('settings.mcp.copy', 'Copiar')}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Note */}
            <div className="p-3 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {t('settings.mcp.note', 'Substitua "SUA_API_KEY" por uma chave criada na seção "API Keys" acima. As ferramentas de geração (imagem, vídeo, blog) rodam de forma assíncrona (jobs).')}
              </p>
            </div>

            {/* Capabilities */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                {t('settings.mcp.capabilitiesTitle', 'Exemplos de recursos')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  t('settings.mcp.capCampaigns', 'Criar campanhas (Google, Meta, LinkedIn)'),
                  t('settings.mcp.capMedia', 'Gerar e editar imagens e vídeos'),
                  t('settings.mcp.capContent', 'Gerar posts, blog e notícias'),
                  t('settings.mcp.capLanding', 'Landing pages e testes A/B'),
                  t('settings.mcp.capFlow', 'Fluxogramas e análise de concorrentes'),
                ].map((cap) => (
                  <div key={cap} className="flex items-center gap-2 p-2 rounded-lg border border-gray-100 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    <span>{cap}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Keys */}
      <Card>
        <CardHeader
          action={
            <Button
              variant="primary"
              size="sm"
              onClick={() => setNewKeyModalOpen(true)}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              {t('settings.api.createKey', 'Create API Key')}
            </Button>
          }
        >
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            {t('settings.api.apiKeys', 'API Keys')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {apiKeys.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Key className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t('settings.api.noKeys', 'No API keys yet')}</p>
              <p className="text-sm mt-1">{t('settings.api.noKeysDesc', 'Create an API key to access the API programmatically')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div
                  key={key.id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      key.is_active
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}>
                      <Key className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">{key.name}</h4>
                        <Badge variant={key.is_active ? 'success' : 'gray'}>
                          {key.is_active ? t('common.active') : t('common.inactive')}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="text-sm text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded font-mono">
                          {key.key_prefix}...
                        </code>
                        <span className="text-xs text-gray-400">
                          {t('settings.api.created', 'Created')} {formatDate(key.created_at)}
                        </span>
                        {key.last_used_at && (
                          <span className="text-xs text-gray-400">
                            • {t('settings.api.lastUsed', 'Last used')} {formatDate(key.last_used_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRegenerateKey(key.id)}
                      title={t('settings.api.regenerate', 'Regenerate')}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteApiKey(key.id)}
                      className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Webhooks */}
      <Card>
        <CardHeader
          action={
            <Button
              variant="primary"
              size="sm"
              onClick={() => setNewWebhookModalOpen(true)}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              {t('settings.api.createWebhook', 'Add Webhook')}
            </Button>
          }
        >
          <CardTitle className="flex items-center gap-2">
            <Webhook className="w-5 h-5" />
            {t('settings.api.webhooks', 'Webhooks')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {webhooks.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Webhook className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t('settings.api.noWebhooks', 'No webhooks configured')}</p>
              <p className="text-sm mt-1">{t('settings.api.noWebhooksDesc', 'Add a webhook to receive real-time notifications')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {webhooks.map((webhook) => (
                <div
                  key={webhook.id}
                  className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        webhook.is_active
                          ? webhook.failure_count > 0
                            ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                            : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                        {webhook.failure_count > 0 ? (
                          <AlertTriangle className="w-5 h-5" />
                        ) : webhook.is_active ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <Webhook className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-medium text-gray-900 dark:text-white font-mono">
                            {webhook.url}
                          </code>
                          <Badge variant={webhook.is_active ? 'success' : 'gray'}>
                            {webhook.is_active ? t('common.active') : t('common.inactive')}
                          </Badge>
                          {webhook.failure_count > 0 && (
                            <Badge variant="warning">
                              {webhook.failure_count} {t('settings.api.failures', 'failures')}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                          <span>{webhook.events.length} {t('settings.api.events', 'events')}</span>
                          {webhook.last_triggered_at && (
                            <span>• {t('settings.api.lastTriggered', 'Last triggered')} {formatDate(webhook.last_triggered_at)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewEvents(webhook)}
                        title={t('settings.api.recentEvents', 'View recent events')}
                      >
                        <History className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTestWebhook(webhook.id)}
                        title={t('settings.api.test', 'Send test')}
                      >
                        {t('settings.api.test', 'Test')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleWebhook(webhook.id, webhook.is_active)}
                      >
                        {webhook.is_active ? t('common.disable') : t('common.enable')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteWebhook(webhook.id)}
                        className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Webhook Secret */}
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {t('settings.api.secret', 'Secret')}:
                      </span>
                      <code className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded font-mono">
                        {showKeyValue === webhook.id ? webhook.secret : '••••••••••••••••'}
                      </code>
                      <button
                        onClick={() => setShowKeyValue(showKeyValue === webhook.id ? null : webhook.id)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showKeyValue === webhook.id ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => copyToClipboard(webhook.secret)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Events */}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {webhook.events.map((event) => (
                      <span
                        key={event}
                        className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-300"
                      >
                        {event}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create API Key Modal */}
      <Modal
        isOpen={newKeyModalOpen}
        onClose={() => {
          setNewKeyModalOpen(false);
          setCreatedKey(null);
        }}
        title={createdKey ? t('settings.api.keyCreatedTitle', 'API Key Created') : t('settings.api.createKey', 'Create API Key')}
      >
        {createdKey ? (
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800 dark:text-yellow-200">
                    {t('settings.api.saveKeyWarning', 'Save this key now!')}
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    {t('settings.api.saveKeyWarningDesc', "You won't be able to see it again.")}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between">
                <code className="text-sm font-mono text-gray-900 dark:text-white break-all">
                  {createdKey}
                </code>
                <button
                  onClick={() => copyToClipboard(createdKey)}
                  className="ml-2 p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => { setNewKeyModalOpen(false); setCreatedKey(null); }}>
                {t('common.done', 'Done')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Input
              label={t('settings.api.keyName', 'Key Name')}
              placeholder={t('settings.api.keyNamePlaceholder', 'e.g., Production API Key')}
              value={newKeyData.name}
              onChange={(e) => setNewKeyData({ ...newKeyData, name: e.target.value })}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.api.expiration', 'Expiration')}
              </label>
              <select
                value={newKeyData.expires_in_days}
                onChange={(e) => setNewKeyData({ ...newKeyData, expires_in_days: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value={0}>{t('settings.api.neverExpires', 'Never expires')}</option>
                <option value={30}>30 {t('common.days', 'days')}</option>
                <option value={90}>90 {t('common.days', 'days')}</option>
                <option value={365}>1 {t('common.year', 'year')}</option>
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setNewKeyModalOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleCreateApiKey} isLoading={isCreating}>
                {t('settings.api.createKey', 'Create API Key')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Create Webhook Modal */}
      <Modal
        isOpen={newWebhookModalOpen}
        onClose={() => setNewWebhookModalOpen(false)}
        title={t('settings.api.createWebhook', 'Add Webhook')}
      >
        <div className="space-y-4">
          <Input
            label={t('settings.api.webhookUrl', 'Webhook URL')}
            placeholder="https://your-server.com/webhook"
            value={newWebhookData.url}
            onChange={(e) => setNewWebhookData({ ...newWebhookData, url: e.target.value })}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('settings.api.selectEvents', 'Select Events')}
            </label>
            <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1">
              {AVAILABLE_EVENTS.map((event) => (
                <label key={event} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newWebhookData.events.includes(event)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewWebhookData({ ...newWebhookData, events: [...newWebhookData.events, event] });
                      } else {
                        setNewWebhookData({ ...newWebhookData, events: newWebhookData.events.filter(ev => ev !== event) });
                      }
                    }}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300 font-mono">{event}</span>
                </label>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => setNewWebhookData({ ...newWebhookData, events: AVAILABLE_EVENTS })}
                className="text-xs text-primary-600 hover:underline"
              >
                {t('common.selectAll', 'Select all')}
              </button>
              <button
                type="button"
                onClick={() => setNewWebhookData({ ...newWebhookData, events: [] })}
                className="text-xs text-gray-500 hover:underline"
              >
                {t('common.clearAll', 'Clear all')}
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setNewWebhookModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateWebhook} isLoading={isCreating}>
              {t('settings.api.createWebhook', 'Add Webhook')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Webhook Events Modal */}
      <Modal
        isOpen={eventsModalOpen}
        onClose={() => {
          setEventsModalOpen(false);
          setSelectedWebhookForEvents(null);
          setWebhookEvents([]);
        }}
        title={t('settings.api.recentEvents', 'Recent Events')}
        size="lg"
      >
        <div className="space-y-4">
          {selectedWebhookForEvents && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              <code>{selectedWebhookForEvents.url}</code>
            </div>
          )}

          {loadingEvents ? (
            <div className="py-8 text-center text-gray-500">
              <Clock className="w-8 h-8 mx-auto mb-2 animate-spin" />
              {t('common.loading')}
            </div>
          ) : webhookEvents.length === 0 ? (
            <div className="py-8 text-center text-gray-500 dark:text-gray-400">
              <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t('settings.api.noEvents', 'No events recorded yet')}</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {webhookEvents.map((event) => (
                <div
                  key={event.id}
                  className={`p-3 rounded-lg border ${
                    event.status === 'success'
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                      : event.status === 'failed'
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {event.status === 'success' ? (
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                      ) : event.status === 'failed' ? (
                        <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                      ) : (
                        <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                      )}
                      <span className="font-mono text-sm font-medium">{event.event_type}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {event.response_code && (
                        <span className={`px-1.5 py-0.5 rounded ${
                          event.response_code >= 200 && event.response_code < 300
                            ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                            : 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                        }`}>
                          HTTP {event.response_code}
                        </span>
                      )}
                      {event.duration_ms && (
                        <span>{event.duration_ms}ms</span>
                      )}
                      <span>{formatDate(event.created_at)}</span>
                    </div>
                  </div>
                  {event.error_message && (
                    <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {event.error_message}
                    </div>
                  )}
                  <details className="mt-2">
                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                      {t('settings.api.eventPayload', 'View Payload')}
                    </summary>
                    <pre className="mt-2 p-2 bg-gray-900 text-gray-100 rounded text-xs overflow-x-auto">
                      {JSON.stringify(event.payload, null, 2)}
                    </pre>
                  </details>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="ghost" onClick={() => {
              setEventsModalOpen(false);
              setSelectedWebhookForEvents(null);
              setWebhookEvents([]);
            }}>
              {t('common.close', 'Close')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default APISettings;
